import React, { useState, useMemo, useContext, useCallback, useEffect, useRef } from 'react';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { CheckCircleIcon, ChevronLeftIcon } from '@heroicons/react/solid';
import Breadcrumb from '../../../components/Breadcrumb';
import CISideNavbar from './components/CISideNavbar';
import { AccordionData } from '../../../constants/data_DF';
import { TriggerGenerateDocumentSection } from '../../../utils/TriggerGenerateDocumentSection';
import ConditionSelectionPanel from './components/ConditionSelectionPanel';
import ConditionSelectionPanelTOC from './components/ConditionSelectionPanelTOC';
import DictationTextArea from '../../../components/DictationTextArea';
import ModuleCompletionDialog, { CancelButton, PrimaryActionButton, GenerateReportButton, SummarySection, DialogMessage } from '../../../components/ModuleCompletionDialog';
import CIFinishPathDialog from '../../../components/CIFinishPathDialog';
import GenerateReportProgressDialog from '../../../components/GenerateReportProgressDialog';
import { auth, db } from '../../../firebase';
import { doc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { isClinicianAssignedToStage, isLeadClinician } from '../../../utils/clinicianPermissions';
import {
  buildFirstGeneratedFields,
  buildFirstCompletedFields,
  appendReportAuditEntry,
} from '../../../utils/reportAuditHelpers';
import {
  buildDeferPadUpdates,
  buildPadInAppointmentUpdates,
} from '../../../utils/appointmentFinishPath';
import { recordInferredAppointmentDateOnCompletion } from '../../../utils/appointmentDateHelpers';
import { useAuth } from '../../../hooks/useAuth';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import { CI_FINISH_PATHS } from '../../../constants/workflowConfig';
import { deriveDiagnosticFormulationConfig } from './config/diagnosticFormulationConfig';
import { buildCIExclusionsContext, getVisibleCISectionTitles, getVisibleCISections } from '../../../utils/clinicalInterviewWorkflow';
import { ADHD_CONDITION_LABEL } from '../../../utils/diagnosticDecisionRouting';
import { resolveDdPadUnlockOnCiComplete } from '../../../utils/moduleUnlockUtils';
// SSI Conflict Detection for 3 Borough / SLaM workflow
import { detectSSIConflictForCI } from '../../../utils/ssiConflictDetection';
import SSIConflictAlert from '../../../components/SSIConflictAlert';
import { TriggerResetSSI } from '../../../utils/TriggerResetSSI';
// Document embed dialog for viewing SSI report
import { DocumentEmbedDialog } from '../../providers/8_diagnosticDecision/components/DocumentEmbedDialog';
import { useSectionExitRegeneration } from '../../../hooks/useSectionExitRegeneration';
import ButtonSpinner from '../../../components/informantManagement/ButtonSpinner';

/** Strip ADHD from emerging-differentials lists — it lives in Referred For. */
const withoutAdhd = (list = []) => list.filter((c) => c !== ADHD_CONDITION_LABEL);

export default function DiagnosticFormulation() {

  const { patientId } = useParams();
  const navigate = useNavigate();
  const { patientDataModel, fetchPatientData, updatePatientFirestore, flushPendingUpdates } = useContext(PatientDataContext);
  const { claims } = useAuth();
  const wf = useWorkflowConfig(patientDataModel, claims);

  // Get organization-specific configuration
  const organisationName = claims?.organisation || 'default';
  const config = deriveDiagnosticFormulationConfig(wf);

  // Route guard: when DF is deferred to after the RDD (WLC/TOC non-prod),
  // skip this CI-based DF page entirely — the user will complete their
  // formulation on the new SSI DF page after the Recommended Diagnosis.
  useEffect(() => {
    if (wf.isDFDeferred) {
      navigate(`/patient/${patientId}/proforma/semi-structured-interview/symptoms`, { replace: true });
    }
  }, [wf.isDFDeferred, patientId, navigate]);

  const currentSectionNumber = 12;
  const currentStageTitle = 'Clinical Interview';
  // The report-generation title MUST remain "Diagnostic Formulation" to match
  // the backend LLM pipeline section identifier regardless of org variant.
  const currentSectionTitle = 'Diagnostic Formulation';
  // SLaM CI Review orgs see this page titled "Review & Synthesis" — it serves
  // the same structural role (final CI section) but with a different UX focus:
  // emerging differentials + formulation + confidential notes, rather than a
  // hierarchical diagnostic classification exercise.
  const displayTitle = wf.hasCIReview ? 'Review & Synthesis' : currentSectionTitle;
  const prevSectionLink = '/proforma/clinical-interview/risk-assessment';
  const prevSectionTitle = 'Risk Assessment';

  // Add canEdit state
  const [canEdit, setCanEdit] = useState(false);

  // Check if the current clinician is assigned to this stage
  useEffect(() => {
    const checkPermissions = async () => {
      const currentUser = auth.currentUser;
      if (currentUser && patientDataModel) {
        const hasEditPermission = isClinicianAssignedToStage(patientDataModel, currentUser.uid, 'CI');
        setCanEdit(hasEditPermission);
      }
    };
    
    checkPermissions();
  }, [patientDataModel]);

  // Whether this org uses the flat "Conditions Under Consideration" layout
  // rather than the hierarchical PPD / SCD / Differentials panels.
  const isFlatModel = wf.isFlatDifferentials;

  // ─── Hierarchical DF state (PPD / SCD / Differentials) ──────────────

  const PANEL_TYPES = {
    PRIMARY: 'primary',
    COMORBID: 'comorbid',
    DIFFERENTIAL: 'differential'
  };

  const FIRESTORE_FIELDS = {
    [PANEL_TYPES.PRIMARY]: 'provisionalPrimaryDiagnosis',
    [PANEL_TYPES.COMORBID]: 'secondaryComorbidDiagnoses',
    [PANEL_TYPES.DIFFERENTIAL]: 'differentialDiagnoses'
  };

  const [selections, setSelections] = useState(() => {
    const sectionData = patientDataModel?.dataModel?.['2_clinicalInterview']?.sections?.[currentSectionNumber] || {};
    return {
      [PANEL_TYPES.PRIMARY]: {
        selected: sectionData.provisionalPrimaryDiagnosis || null,
        isConfirmed: !!sectionData.provisionalPrimaryDiagnosis,
      },
      [PANEL_TYPES.COMORBID]: {
        selected: sectionData.secondaryComorbidDiagnoses || [],
        isConfirmed: !!(sectionData.secondaryComorbidDiagnoses?.length),
      },
      [PANEL_TYPES.DIFFERENTIAL]: {
        selected: sectionData.differentialDiagnoses || [],
        isConfirmed: !!(sectionData.differentialDiagnoses?.length),
      }
    };
  });

  // ─── Flat DF state (single "Conditions Under Consideration" list) ───
  // For CI Review orgs (SLaM), prefer reading from emergingFormulations.leadClinician
  // which is the canonical dual-write target. Falls back to CI section 12 for
  // patients who haven't yet written to the new location.

  const [flatDifferentials, setFlatDifferentials] = useState(() => {
    const leadData = patientDataModel?.dataModel?.emergingFormulations?.leadClinician;
    const sectionData = patientDataModel?.dataModel?.['2_clinicalInterview']?.sections?.[currentSectionNumber] || {};
    const raw = (wf.hasCIReview && leadData?.differentials?.length)
      ? leadData.differentials
      : (sectionData.differentialDiagnoses || []);
    // CI Review dual-panel: ADHD is Condition Referred For, never an emerging differential.
    // Strip legacy saved ADHD so it cannot reappear in the Emerging Differentials list.
    const saved = wf.hasCIReview ? withoutAdhd(raw) : raw;
    return {
      selected: saved,
      isConfirmed: !!(saved.length),
    };
  });

  // Accordion list with ADHD removed — used only by SLaM CI Review Emerging Differentials.
  const emergingDifferentialConditions = useMemo(() => {
    const result = {};
    for (const [category, conditions] of Object.entries(AccordionData)) {
      const filtered = conditions.filter((c) => c.icd_11_label !== ADHD_CONDITION_LABEL);
      if (filtered.length > 0) result[category] = filtered;
    }
    return result;
  }, []);

  // Formulation notes — shown for MDT review orgs and CI Review orgs (SLaM).
  const [additionalNotes, setAdditionalNotes] = useState('');

  // Confidential notes — only for CI Review orgs (SLaM). Private clinician
  // observations not included in any patient-facing report.
  const [confidentialNotes, setConfidentialNotes] = useState('');

  // Refs that always hold the latest note values — used by blur handlers
  // to avoid stale-closure reads.
  const additionalNotesRef = useRef(additionalNotes);
  useEffect(() => { additionalNotesRef.current = additionalNotes; }, [additionalNotes]);
  const confidentialNotesRef = useRef(confidentialNotes);
  useEffect(() => { confidentialNotesRef.current = confidentialNotes; }, [confidentialNotes]);

  // Whether the notes sections should be visible. Shown for MDT review orgs
  // (TOC) and CI Review orgs (SLaM where the Lead Clinician records
  // formulation/confidential notes on this page).
  const showNotesSections = wf.isMDTReview || wf.hasCIReview;

  // One-shot hydration — only runs on initial mount to seed local state
  // from Firestore. Does NOT re-run on subsequent patientDataModel refreshes
  // (which would clobber in-progress typed-but-unblurred notes).
  const isNotesInitialized = useRef(false);

  useEffect(() => {
    if (!isFlatModel || !showNotesSections) return;
    if (isNotesInitialized.current) return;

    if (wf.hasCIReview) {
      // SLaM: read from emergingFormulations.leadClinician (canonical) with
      // fallback to CI section 12 for first-time access after migration.
      const leadData = patientDataModel?.dataModel?.emergingFormulations?.leadClinician;
      const sectionData = patientDataModel?.dataModel?.['2_clinicalInterview']?.sections?.[currentSectionNumber];
      if (leadData || sectionData) {
        isNotesInitialized.current = true;
        setAdditionalNotes(leadData?.formulation || sectionData?.clinicianNotes?.['Additional Notes'] || '');
        setConfidentialNotes(leadData?.confidentialNotes || '');
      }
    } else {
      // TOC/legacy: read from CI section 12 only
      const sectionData = patientDataModel?.dataModel?.['2_clinicalInterview']?.sections?.[currentSectionNumber];
      if (sectionData) {
        isNotesInitialized.current = true;
        setAdditionalNotes(sectionData.clinicianNotes?.['Additional Notes'] || '');
      }
    }
  }, [patientDataModel, currentSectionNumber, isFlatModel, showNotesSections, wf.hasCIReview]);

  // Update Firestore whenever selections change
  const updateFirestore = useCallback((updates) => {
    updatePatientFirestore(patientId, {
      '2_clinicalInterview': {
        sections: {
          [currentSectionNumber]: updates
        }
      }
    });
  }, [patientId, currentSectionNumber, updatePatientFirestore]);

  const handleSelect = (panelType, value) => {
    if (!canEdit) return;

    setSelections(prev => {
      const newSelections = { ...prev };

      if (panelType === PANEL_TYPES.PRIMARY) {
        // Toggle selection for primary diagnosis
        const newValue = prev[PANEL_TYPES.PRIMARY].selected === value ? null : value;
        newSelections[PANEL_TYPES.PRIMARY] = {
          ...prev[PANEL_TYPES.PRIMARY],
          selected: newValue,
        };
      } else {
        // Toggle selection for multi-select panels
        const currentSelections = prev[panelType].selected;
        const newValues = currentSelections.includes(value)
          ? currentSelections.filter(item => item !== value)
          : [...currentSelections, value];

        newSelections[panelType] = {
          ...prev[panelType],
          selected: newValues,
        };
      }

      return newSelections;
    });
    setHasChanges(true);
  };

  const handleConfirm = (panelType) => {
    if (!canEdit) return;

    setSelections(prev => {
      const newSelections = { ...prev };

      if (panelType === PANEL_TYPES.PRIMARY) {
        const primaryDiagnosis = prev[PANEL_TYPES.PRIMARY].selected;

        // Update primary panel
        newSelections[PANEL_TYPES.PRIMARY] = {
          ...prev[PANEL_TYPES.PRIMARY],
          isConfirmed: true
        };

        // Remove primary from other panels and reset if empty
        const updatedComorbid = prev[PANEL_TYPES.COMORBID].selected
          .filter(item => item !== primaryDiagnosis);

        newSelections[PANEL_TYPES.COMORBID] = {
          selected: updatedComorbid,
          isConfirmed: updatedComorbid.length > 0 ? prev[PANEL_TYPES.COMORBID].isConfirmed : false
        };

        // Only update differential diagnoses if the panel is visible
        const firestoreUpdates = {
          [FIRESTORE_FIELDS[PANEL_TYPES.PRIMARY]]: primaryDiagnosis,
          [FIRESTORE_FIELDS[PANEL_TYPES.COMORBID]]: updatedComorbid,
        };

        if (config.panels.differentialDiagnoses.visible) {
          const updatedDifferential = prev[PANEL_TYPES.DIFFERENTIAL].selected
            .filter(item => item !== primaryDiagnosis);

        newSelections[PANEL_TYPES.DIFFERENTIAL] = {
          selected: updatedDifferential,
          isConfirmed: updatedDifferential.length > 0 ? prev[PANEL_TYPES.DIFFERENTIAL].isConfirmed : false
        };

          firestoreUpdates[FIRESTORE_FIELDS[PANEL_TYPES.DIFFERENTIAL]] = updatedDifferential;
        }

        // Update Firestore with relevant changes
        updateFirestore(firestoreUpdates);
      } else {
        // For other panels, just update the current panel
        newSelections[panelType] = {
          ...prev[panelType],
          isConfirmed: true
        };

        // Update Firestore for this panel only
        updateFirestore({
          [FIRESTORE_FIELDS[panelType]]: prev[panelType].selected
        });
      }

      return newSelections;
    });
    setHasChanges(true);
  };


  const handleChangeChoice = (panelType) => {
    if (!canEdit) return;

    setSelections(prev => ({
      ...prev,
      [panelType]: {
        ...prev[panelType],
        isConfirmed: false
      }
    }));
    setHasChanges(true);
  };

  // ─── Flat DF handlers ───────────────────────────────────────────────

  const handleFlatSelect = (value) => {
    if (!canEdit) return;
    // Defence in depth: ignore ADHD picks on CI Review (referred-for only).
    if (wf.hasCIReview && value === ADHD_CONDITION_LABEL) return;
    setFlatDifferentials(prev => {
      const current = prev.selected;
      const newValues = current.includes(value)
        ? current.filter(item => item !== value)
        : [...current, value];
      return {
        ...prev,
        selected: wf.hasCIReview ? withoutAdhd(newValues) : newValues,
      };
    });
    setHasChanges(true);
  };

  const handleFlatConfirm = () => {
    if (!canEdit) return;
    const toSave = wf.hasCIReview
      ? withoutAdhd(flatDifferentials.selected)
      : flatDifferentials.selected;
    setFlatDifferentials(prev => ({ ...prev, selected: toSave, isConfirmed: true }));
    updateFirestore({ differentialDiagnoses: toSave });
    sendEmergingFormulationsUpdate({ differentials: toSave });
    setHasChanges(true);
  };

  const handleFlatChangeChoice = () => {
    if (!canEdit) return;
    setFlatDifferentials(prev => ({ ...prev, isConfirmed: false }));
    setHasChanges(true);
  };

  // Updates local state AND enqueues a debounced Firestore write on every
  // keystroke. Ensures data is persisted even if the page navigates before
  // blur fires. The 200ms debounce in PatientDataContext coalesces rapid input.
  const handleAdditionalNotesChange = (value) => {
    if (!canEdit) return;
    setAdditionalNotes(value);
    setHasChanges(true);
    sendSectionUpdates({ clinicianNotes: { 'Additional Notes': value } });
    sendEmergingFormulationsUpdate({ formulation: value });
  };

  // Reads from additionalNotesRef.current so we always persist the latest
  // keystroke even if blur fires before React re-renders (stale-closure guard).
  const handleAdditionalNotesBlur = () => {
    if (!canEdit) return;
    sendSectionUpdates({ clinicianNotes: { 'Additional Notes': additionalNotesRef.current } });
    sendEmergingFormulationsUpdate({ formulation: additionalNotesRef.current });
  };

  // Confidential notes change/blur — writes only to emergingFormulations
  // (no legacy CI section 12 equivalent exists for confidential notes).
  const handleConfidentialNotesChange = (value) => {
    if (!canEdit) return;
    setConfidentialNotes(value);
    setHasChanges(true);
    sendEmergingFormulationsUpdate({ confidentialNotes: value });
  };

  const handleConfidentialNotesBlur = () => {
    if (!canEdit) return;
    sendEmergingFormulationsUpdate({ confidentialNotes: confidentialNotesRef.current });
  };

  const user = auth.currentUser;
  const clinicianId = user?.uid;

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  // Path 1 confirm write (workflowDecisions + provisional PAD + CI complete).
  const [isFinishPathSubmitting, setIsFinishPathSubmitting] = useState(false);

  // SSI Conflict Alert state (3 Borough / SLaM workflow)
  // This dialog is shown when the Lead clinician's provisional diagnosis conflicts with SSI recommendation
  const [isConflictAlertOpen, setIsConflictAlertOpen] = useState(false);
  const [conflictDetails, setConflictDetails] = useState(null);
  const [isResettingSSI, setIsResettingSSI] = useState(false);

  // SSI Report embed dialog state (for viewing SSI report from conflict alert)
  const [isSSIReportDialogOpen, setIsSSIReportDialogOpen] = useState(false);

  // Get SSI report last generated timestamp for the embed dialog
  const ssiLastGenerated = useMemo(() => 
    patientDataModel?.documents?.semistructuredinterviewReport?.lastGenerated || null, 
    [patientDataModel]
  );

  // Check if current user is the Lead clinician (relevant for 3 Borough / SLaM conflict detection)
  const isCurrentUserLead = useMemo(() => {
    if (!patientDataModel || !clinicianId) return false;
    return isLeadClinician(patientDataModel, clinicianId);
  }, [patientDataModel, clinicianId]);

  // Check if this organisation uses SSI-first workflow (3 Borough / SLaM)
  const isSSIFirstOrg = wf.isSSIFirst;

  // Navigation helpers — for flat model the next step is always the
  // same regardless of condition selection (SSI-first orgs go to DD,
  // CI-first orgs go to SSI).  Hierarchical model routes depend on
  // whether PPD = ADHD.
  const getNextRoute = useCallback(() => {
    if (isFlatModel || !config.navigation.useAdhdFlowDecision) {
      return config.navigation.defaultRoute;
    }
    const isAdhd = selections[PANEL_TYPES.PRIMARY].selected === 'Attention Deficit Hyperactivity Disorder (ADHD)';
    return isAdhd ? config.navigation.adhdRoute : config.navigation.nonAdhdRoute;
  }, [isFlatModel, config.navigation, selections, PANEL_TYPES.PRIMARY]);

  const getNextButtonText = useCallback(() => {
    if (isFlatModel || !config.navigation.useAdhdFlowDecision) {
      return config.navigation.defaultButtonText;
    }
    const isAdhd = selections[PANEL_TYPES.PRIMARY].selected === 'Attention Deficit Hyperactivity Disorder (ADHD)';
    return isAdhd ? config.navigation.adhdButtonText : config.navigation.nonAdhdButtonText;
  }, [isFlatModel, config.navigation, selections, PANEL_TYPES.PRIMARY]);

  const shouldShowGenerateReportButton = useCallback(() => {
    // Flat model orgs always proceed to SSI (no early report generation from CI DF).
    if (isFlatModel) return false;

    if (!config.navigation.useAdhdFlowDecision) return false;
    
    return selections[PANEL_TYPES.PRIMARY].selected === 'Attention Deficit Hyperactivity Disorder (ADHD)';
  }, [isFlatModel, config.navigation, selections, PANEL_TYPES.PRIMARY]);

  const getDialogMessage = useCallback(() => {
    if (isFlatModel || !config.navigation.useAdhdFlowDecision) {
      return `Please review your selections. Are you ready to proceed?`;
    }
    
    const isAdhd = selections[PANEL_TYPES.PRIMARY].selected === 'Attention Deficit Hyperactivity Disorder (ADHD)';
    return isAdhd 
      ? "Please review your selections. Are you happy to proceed with generating the report?"
      : "Given your provisional primary diagnosis is not ADHD, please proceed to the Diagnostic Decision in order to provide recommended next steps on your provisional primary diagnosis and any secondary and/or comorbid diagnoses you have identified.";
  }, [isFlatModel, config.navigation, selections, PANEL_TYPES.PRIMARY]);

  const constructDiagnosisSummary = useCallback(() => {
    if (isFlatModel) {
      if (flatDifferentials.selected.length === 0) {
        return <div className="text-gray-500 italic">No conditions selected</div>;
      }
      return flatDifferentials.selected.map((condition, i) => (
        <div key={i} className="mb-1">• {condition}</div>
      ));
    }

    let output = [];
    output.push(`• Provisional Primary Diagnosis: ${selections[PANEL_TYPES.PRIMARY].selected}`);

    if (selections[PANEL_TYPES.COMORBID].selected.length > 0) {
      output.push(`• Provisional Secondary and/or Comorbid Diagnoses: ${selections[PANEL_TYPES.COMORBID].selected.join(', ')}`);
    }

    if (config.panels.differentialDiagnoses.visible && selections[PANEL_TYPES.DIFFERENTIAL].selected.length > 0) {
      output.push(`• Differential Diagnoses: ${selections[PANEL_TYPES.DIFFERENTIAL].selected.join(', ')}`);
    }

    return output.map((bullet, index) => (
      <div key={index} className="mb-1">
        {bullet}
      </div>
    ));
  }, [isFlatModel, flatDifferentials, selections, config.panels.differentialDiagnoses.visible, PANEL_TYPES.PRIMARY, PANEL_TYPES.COMORBID, PANEL_TYPES.DIFFERENTIAL]);


  const { sections: ciSections, excludedKeys } = buildCIExclusionsContext(patientDataModel, organisationName);
  const SECTIONS_CI = useMemo(() => getVisibleCISections(ciSections, excludedKeys), [ciSections, excludedKeys]);
  const currentSectionIndex = useMemo(() => SECTIONS_CI.findIndex(s => s.key === '12'), [SECTIONS_CI]);

  const sendSectionUpdates = useCallback((newUpdates) => {
    if (!canEdit) return Promise.resolve();

    return updatePatientFirestore(patientId, {
      '2_clinicalInterview': {
        sections: {
          [currentSectionNumber]: newUpdates
        }
      }
    });
  }, [patientId, currentSectionNumber, updatePatientFirestore, canEdit]);

  /**
   * Dual-write helper for CI Review orgs (SLaM). Mirrors differentials,
   * formulation, and confidential notes to dataModel.emergingFormulations.leadClinician
   * with attribution metadata. The Lead Clinician's data is written here so
   * that DD Review pages can render both clinicians' inputs with attribution,
   * while the legacy CI section 12 write is preserved for backward-compatible
   * report generation by the existing LLM pipeline.
   */
  const sendEmergingFormulationsUpdate = useCallback((overrides = {}) => {
    if (!canEdit || !wf.hasCIReview) return Promise.resolve();

    const payload = {
      differentials: overrides.differentials ?? flatDifferentials.selected,
      formulation: overrides.formulation ?? additionalNotesRef.current,
      confidentialNotes: overrides.confidentialNotes ?? confidentialNotesRef.current,
      updatedAt: serverTimestamp(),
      updatedBy: auth.currentUser?.uid || null,
      updatedByName: auth.currentUser?.displayName || null,
    };

    return updatePatientFirestore(patientId, {
      emergingFormulations: {
        leadClinician: payload,
      }
    });
  }, [canEdit, wf.hasCIReview, flatDifferentials.selected, patientId, updatePatientFirestore]);

  const [hasChanges, setHasChanges] = useState(false);

  // DF paragraph can only be generated from a confirmed form.
  // Flat model: differentials panel confirmed. Hierarchical: PPD confirmed.
  const isFormComplete = isFlatModel
    ? flatDifferentials.isConfirmed
    : (selections[PANEL_TYPES.PRIMARY].isConfirmed && !!selections[PANEL_TYPES.PRIMARY].selected);

  const { markRegenHandled } = useSectionExitRegeneration({
    patientId,
    stageTitle: currentStageTitle,
    sectionTitle: currentSectionTitle,
    hasChanges,
    canEdit,
    patientDataModel,
    shouldRegenerate: isFormComplete,
  });

  const handleSectionCompletion = useCallback((patientId, currentStageTitle, currentSectionTitle, hasChanges) => {
    if (!canEdit) return Promise.resolve();
    if (!isFormComplete) return Promise.resolve();

    const currentSectionData = patientDataModel?.dataModel?.['2_clinicalInterview']
      ?.sections?.[currentSectionNumber];
    const isAlreadyCompleted = currentSectionData?.status === 'completed';

    const sectionsAlreadyGenerated = patientDataModel?.dataModel?.['6_manageReports']?.sections?.['1']?.stageSectionsGenerated;
    const isCurrentSectionGenerated = sectionsAlreadyGenerated?.includes(currentSectionTitle) || false;

    const needsStatusWrite = !isAlreadyCompleted;
    const needsGeneration = hasChanges || !isCurrentSectionGenerated;

    if (!needsStatusWrite && !needsGeneration) {
      return Promise.resolve();
    }

    // For flat model, persist the differentials list and any notes alongside
    // the status update so a single write captures the final state.
    if (needsStatusWrite) {
      const completionData = { status: 'completed', progress: 100 };
      if (isFlatModel) {
        const differentialsToSave = wf.hasCIReview
          ? withoutAdhd(flatDifferentials.selected)
          : flatDifferentials.selected;
        completionData.differentialDiagnoses = differentialsToSave;
        if (showNotesSections) {
          completionData.clinicianNotes = { 'Additional Notes': additionalNotes };
        }
      }
      sendSectionUpdates(completionData);
    }

    // Dual-write: persist final state to emergingFormulations.leadClinician
    // so that DD Review pages can render both clinicians' data with attribution.
    // CI Review: strip ADHD so Emerging Differentials never re-introduce it.
    sendEmergingFormulationsUpdate(
      wf.hasCIReview
        ? { differentials: withoutAdhd(flatDifferentials.selected) }
        : undefined
    );

    const dataReady = flushPendingUpdates({ noTimeout: true });

    if (needsGeneration) {
      TriggerGenerateDocumentSection(patientId, currentStageTitle, currentSectionTitle, [], dataReady);
    }

    return dataReady;
  }, [sendSectionUpdates, sendEmergingFormulationsUpdate, patientDataModel, canEdit, flushPendingUpdates, isFormComplete, isFlatModel, flatDifferentials, additionalNotes, showNotesSections, currentSectionNumber, wf.hasCIReview]);

  const handleGenerateReport = () => {
    if (!canEdit) return;

    setIsDialogOpen(false);
    setIsGenerating(true);
  };

  /**
   * Path 2 — record deferPad on the patient root (overview flips to DD ahead of
   * PAD), then open the existing CI report progress dialog → Documents.
   * workflowDecisions lives outside dataModel, so this cannot use updatePatientFirestore.
   */
  const handleGenerateReportDeferPad = useCallback(async () => {
    if (!canEdit) return;
    setIsFinishPathSubmitting(true);
    try {
      await runTransaction(db, async (transaction) => {
        const docRef = doc(db, 'patients', patientId);
        transaction.update(docRef, buildDeferPadUpdates());
      });
      setIsDialogOpen(false);
      setIsGenerating(true);
    } catch (error) {
      console.error('Failed to set CI finish path (deferPad):', error);
      alert('Could not save your choice. Please try again.');
    } finally {
      setIsFinishPathSubmitting(false);
    }
  }, [canEdit, patientId]);

  /**
   * Path 1 — write ciFinishPath + provisional PAD outcome, unlock PAD and DD if
   * still locked (SLaM: modules open as soon as CI is first completed — report
   * approval stays a Documents gate only), mark CI complete, and navigate
   * straight into PAD Review. Missing CI paragraphs were already kicked off in
   * the background by handleNextSection before this dialog opened.
   */
  const handleConfirmPadPath = useCallback(async ({ provisionalAdhdOutcome }) => {
    if (!canEdit) return;
    setIsFinishPathSubmitting(true);
    try {
      // The update map (CI completion + ciReport* generated fields + DD/PAD
      // unlock + provisional outcome) is built by the shared helper so the SSI
      // entry point used by one-clinician SLaM patients writes the same shape.
      const { updates, didWriteFirstCompleted, isFirstGeneration } =
        buildPadInAppointmentUpdates({
          module: 'ci',
          patientDataModel,
          user: auth.currentUser,
          provisionalAdhdOutcome,
        });

      await runTransaction(db, async (transaction) => {
        const docRef = doc(db, 'patients', patientId);
        transaction.update(docRef, updates);
      });

      appendReportAuditEntry(patientId, {
        module: 'ci',
        eventType: 'generated',
        isFirstOccurrence: isFirstGeneration,
      });

      // Inferred appointment audit on first CI completion (fire-and-forget; does
      // not write appointmentDates). Mirrors handleNextPageButtonClick so Path 1
      // records the CI appointment date the same way Path 2 does.
      recordInferredAppointmentDateOnCompletion({
        patientId,
        moduleKey: 'ci',
        didWriteFirstCompleted,
        enabled: wf.isAppointmentDatesEnabled,
      });

      setIsDialogOpen(false);
      navigate(
        `/patient/${patientId}/proforma/post-assessment-discussion/review-assessment`
      );
    } catch (error) {
      console.error('Failed to start PAD-in-appointment path:', error);
      alert('Could not start the Post-Assessment Discussion. Please try again.');
    } finally {
      setIsFinishPathSubmitting(false);
    }
  }, [canEdit, patientId, patientDataModel, navigate, wf.isAppointmentDatesEnabled]);

  const handleCancelGeneration = () => {
    setIsGenerating(false);
  };


  // SSI Conflict Alert handlers (3 Borough / SLaM workflow)
  
  /**
   * Handle closing the conflict alert
   * If closed without action, show the normal completion dialog
   */
  const handleConflictAlertClose = useCallback(() => {
    setIsConflictAlertOpen(false);
    setConflictDetails(null);
  }, []);

  /**
   * Handle viewing the SSI report from the conflict alert
   * Opens the embedded SSI report dialog (same as supporting documents panel in DD)
   */
  const handleViewSSIReport = useCallback(() => {
    console.log('View SSI Report clicked - opening embedded dialog');
    setIsSSIReportDialogOpen(true);
  }, []);

  /**
   * Handle navigation after CI completion
   * Updates Firestore with completion status and navigates to the specified route.
   *
   * SLaM / 3 Borough (hasCIReview or SSI-first): also unlock DD and PAD when
   * they are still locked so both modules are accessible as soon as CI is
   * first completed — used by Path 2 (defer PAD → Generate Report → Documents)
   * and any non-wizard SSI-first finish that still hits this path.
   */
  const handleNextPageButtonClick = useCallback(async (route) => {
    const manageReports = patientDataModel?.dataModel?.['6_manageReports'];
    const unlockSsiFirstDdPad =
      wf.hasCIReview || wf.isSSIFirst
        ? resolveDdPadUnlockOnCiComplete(patientDataModel)
        : { unlockPad: false, unlockDd: false };
    const firstCompletedFields = buildFirstCompletedFields(patientDataModel?.dataModel?.['2_clinicalInterview']);

    const updates = {
      '2_clinicalInterview': {
        status: 'completed',
        progress: 100,
        docs_available: true,
        lastCompletedAt: serverTimestamp(),
        lastCompletedBy: auth.currentUser.uid,
        lastCompletedByName: auth.currentUser.displayName,
        ...firstCompletedFields,
      },
      '6_manageReports': {
        ciReportGenerated: true,
        ciReportGeneratedAt: serverTimestamp(),
        ciReportGeneratedBy: auth.currentUser.uid,
        ciReportGeneratedByName: auth.currentUser.displayName,
        ...buildFirstGeneratedFields('ci', manageReports),
      },
    };

    if (unlockSsiFirstDdPad.unlockPad) {
      updates['5_postAssessmentDiscussion'] = { status: 'notStarted' };
    }
    if (unlockSsiFirstDdPad.unlockDd) {
      updates['4_diagnosticDecision'] = { status: 'notStarted' };
    }

    await updatePatientFirestore(patientId, updates);

    appendReportAuditEntry(patientId, {
      module: 'ci',
      eventType: 'generated',
      isFirstOccurrence: !manageReports?.ciReportFirstGeneratedAt,
    });

    // Inferred appointment audit on first completion (fire-and-forget; does not write appointmentDates)
    recordInferredAppointmentDateOnCompletion({
      patientId,
      moduleKey: 'ci',
      didWriteFirstCompleted: Object.keys(firstCompletedFields).length > 0,
      enabled: wf.isAppointmentDatesEnabled,
    });

    navigate(route);
  }, [patientId, updatePatientFirestore, navigate, patientDataModel, wf.hasCIReview, wf.isSSIFirst, wf.isAppointmentDatesEnabled]);

  /**
   * Handle resetting the SSI section for re-editing.
   * Triggers the cloud function to archive/reset SSI data.
   *
   * SLaM CI Review (hasCIReview): reopen the finish-path wizard so the Lead
   * still chooses PAD-now vs defer — never hard-navigate to DD.
   * Other SSI-first orgs: keep the previous "continue to next route" behaviour.
   */
  const handleResetSSI = useCallback(async () => {
    setIsResettingSSI(true);
    try {
      console.log('Reset SSI requested with conflict details:', conflictDetails);
      
      // Call the resetSSIForLeadClinician cloud function
      const result = await TriggerResetSSI(patientId, conflictDetails);
      
      if (result.success) {
        console.log('SSI Reset successful:', result);
        
        // Refresh patient data to get the updated state
        await fetchPatientData(patientId);
        
        // Close the conflict alert
        setIsConflictAlertOpen(false);
        setConflictDetails(null);

        if (wf.hasCIReview) {
          setIsDialogOpen(true);
          return;
        }

        // Legacy SSI-first (non CI-Review): proceed to the configured next route
        await handleNextPageButtonClick(`/patient/${patientId}${getNextRoute()}`);
      } else {
        console.error('SSI Reset failed:', result.message);
        alert(`Failed to reset SSI: ${result.message}`);
      }
    } catch (error) {
      console.error('Error resetting SSI:', error);
      alert('An error occurred while resetting SSI. Please try again.');
    } finally {
      setIsResettingSSI(false);
    }
  }, [conflictDetails, patientId, fetchPatientData, handleNextPageButtonClick, getNextRoute, wf.hasCIReview]);

  const [isSaving, setIsSaving] = useState(false);

  const handleNextSection = useCallback(async () => {
    markRegenHandled();
    setIsSaving(true);
    // Await here because fetchPatientData needs committed data to read
    // back the latest stageSectionsGenerated for the bulk trigger check.
    await handleSectionCompletion(patientId, currentStageTitle, currentSectionTitle, hasChanges);
    await fetchPatientData(patientId);

    const sectionsAlreadyGenerated = patientDataModel?.dataModel?.['6_manageReports']?.sections?.['1']?.stageSectionsGenerated || [];

    // Build the list of CI sections for which AI paragraphs should exist, excluding:
    // - Hidden sections (e.g., Mental Status Examination for Psychological Consultations)
    // - Diagnostic Formulation (the current page)
    const { sections, excludedKeys } = buildCIExclusionsContext(patientDataModel, organisationName);
    const visibleTitlesAll = getVisibleCISectionTitles(sections, excludedKeys);
    const sectionTitlesCI = visibleTitlesAll.filter(title =>
      title !== 'Patient Information' && title !== 'Diagnostic Formulation'
    );
    const sectionsNotGenerated = sectionTitlesCI.filter(sectionTitle => !sectionsAlreadyGenerated.includes(sectionTitle));

    if (sectionsNotGenerated.length > 0) {
      console.log("Sections not yet generated:", sectionsNotGenerated);
      // Data is already confirmed committed from the await above, so
      // pass a resolved Promise — no delay needed.
      sectionsNotGenerated.forEach(sectionTitle =>
        TriggerGenerateDocumentSection(patientId, currentStageTitle, sectionTitle, [], Promise.resolve())
      );
    }

    setIsSaving(false);

    // Guard: if SLaM already chose a finish path, do not re-run the wizard
    // (avoids flipping Path 1 ↔ Path 2 after CI is already completed).
    if (wf.hasCIReview && wf.ciFinishPath) {
      if (wf.ciFinishPath === CI_FINISH_PATHS.PAD_IN_APPOINTMENT) {
        await handleNextPageButtonClick(
          `/patient/${patientId}/proforma/post-assessment-discussion/review-assessment`
        );
        return;
      }
      if (wf.ciFinishPath === CI_FINISH_PATHS.DEFER_PAD) {
        const alreadyGenerated =
          patientDataModel?.dataModel?.['6_manageReports']?.ciReportGenerated;
        if (alreadyGenerated) {
          navigate(`/patient/${patientId}/review-document-ci`);
        } else {
          setIsGenerating(true);
        }
        return;
      }
    }

    // 3 Borough / SLaM workflow: Check for SSI conflict before showing the completion dialog.
    // CI Review dual-panel: ADHD is always the referred-for condition (not in
    // Emerging Differentials), so conflict detection uses ADHD_CONDITION_LABEL.
    // Other flat orgs still check whether ADHD is in Conditions Under Consideration.
    if (isSSIFirstOrg && isCurrentUserLead) {
      const provisionalDiagnosis = wf.hasCIReview
        ? ADHD_CONDITION_LABEL
        : isFlatModel
          ? (flatDifferentials.selected.includes(ADHD_CONDITION_LABEL)
            ? ADHD_CONDITION_LABEL : null)
          : selections[PANEL_TYPES.PRIMARY].selected;
      const conflict = detectSSIConflictForCI(patientDataModel, organisationName, provisionalDiagnosis, wf);
      
      if (conflict.hasConflict && conflict.shouldShowConflictUI) {
        setConflictDetails(conflict);
        setIsConflictAlertOpen(true);
        return;
      }
    }

    setIsDialogOpen(true);

  }, [patientId, hasChanges, handleSectionCompletion, patientDataModel, fetchPatientData, organisationName, isSSIFirstOrg, isCurrentUserLead, selections, PANEL_TYPES.PRIMARY, isFlatModel, flatDifferentials, markRegenHandled, wf, handleNextPageButtonClick, navigate]);


  const handlePrevSection = useCallback(async () => {
    setIsSaving(true);
    markRegenHandled();
    await handleSectionCompletion(patientId, currentStageTitle, currentSectionTitle, hasChanges);
    navigate(`/patient/${patientId}${prevSectionLink}`);
  }, [patientId, navigate, prevSectionLink, hasChanges, handleSectionCompletion, markRegenHandled]);


  const navigateToSection = useCallback(async (index) => {
    setIsSaving(true);
    markRegenHandled();
    await handleSectionCompletion(patientId, currentStageTitle, currentSectionTitle, hasChanges);
    const section = SECTIONS_CI[index];
    if (section.route) {
      navigate(`/patient/${patientId}${section.route}`);
    }
  }, [patientId, navigate, SECTIONS_CI, hasChanges, handleSectionCompletion, markRegenHandled]);

  const breadcrumbTuples = [
    ["Patient Overview", `/patient/${patientId}`],
    [currentStageTitle],
    [displayTitle]
  ];
  // isFormComplete (defined above alongside the exit-regen hook) doubles as
  // the form-readiness flag for the "Finish Clinical Interview" button.

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={breadcrumbTuples} />
      <div className="flex">
        <CISideNavbar
          sections={SECTIONS_CI}
          currentSectionIndex={currentSectionIndex}
          navigateToSection={navigateToSection}
          isNavigating={isSaving}
          wf={wf}
        />
        <div className="flex-grow p-8">
          <div className="flex justify-between items-center mb-6">
            <button
              onClick={handlePrevSection}
              disabled={isSaving}
              className={`flex items-center py-2 px-4 mb-2 rounded-md shadow-md text-gray-900 border border-gray-300 transition-all duration-300 ${isSaving ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-50'}`}
            >
              {isSaving
                ? <ButtonSpinner className="w-5 h-5 mr-2" />
                : <ChevronLeftIcon className="w-5 h-5 mr-2" aria-hidden="true" />}
              <span className="font-bold">{isSaving ? 'Loading...' : `Back to ${prevSectionTitle}`}</span>
            </button>
            {canEdit && (
            <button
              onClick={handleNextSection}
              className={`flex items-center py-2 px-4 rounded-md shadow-md transition-all duration-300 ${isFormComplete && !isSaving
                ? 'hover:bg-violet-800 transition duration-100 bg-violet-900 text-white'
                : isSaving ? 'opacity-60 cursor-not-allowed bg-violet-900 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              disabled={!isFormComplete || isSaving}
            >
              <span className="font-bold">{isSaving ? "Saving Progress..." : `Finish Clinical Interview`}</span>
              {isSaving
                ? <ButtonSpinner className="w-5 h-5 ml-2" />
                : <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
            </button>
            )}
          </div>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">{currentSectionNumber}. {displayTitle}</h2>
          </div>

          {isFlatModel ? (
            /* ─── Flat DF: Conditions panel(s) ─── */
            <>
              {wf.hasCIReview ? (
                /* SLaM CI Review: Condition(s) Referred For (ADHD) + Emerging Differentials */
                <div className="flex flex-col gap-6">
                  <div className="bg-gray-50 rounded-2xl border border-gray-200 text-left p-6 shadow-md">
                    <h3 className="font-bold text-lg mb-2">Condition(s) Referred For</h3>
                    <div className="mt-3 p-3 bg-gray-100 border border-gray-200 rounded-lg">
                      <p className="text-base text-gray-700 flex items-start gap-2">
                        <span className="text-gray-400 mt-0.5">•</span>
                        <span className="font-semibold">{ADHD_CONDITION_LABEL}</span>
                      </p>
                    </div>
                  </div>

                  <ConditionSelectionPanelTOC
                    title="Emerging Differentials"
                    variant="non-report"
                    searchable
                    showSelectedBullets
                    subtitle="Conditions you're actively considering as alternative explanations, co-occurring conditions, or to rule in/out when you finalise your Diagnostic Decision. ADHD is already recorded as the condition referred for."
                    selectedOptions={withoutAdhd(flatDifferentials.selected)}
                    isMultiSelect={true}
                    isConfirmed={flatDifferentials.isConfirmed}
                    onSelect={handleFlatSelect}
                    onConfirm={handleFlatConfirm}
                    onChangeChoice={handleFlatChangeChoice}
                    conditions={emergingDifferentialConditions}
                    allowEmptyConfirm={true}
                    canEdit={canEdit}
                  />
                </div>
              ) : (
                <div className="flex flex-col gap-6">
                  <ConditionSelectionPanel
                    title="Conditions Under Consideration"
                    subtitle="Select any conditions you are currently considering as part of your differential diagnosis. You can select multiple conditions."
                    selectedOptions={flatDifferentials.selected}
                    isMultiSelect={true}
                    isConfirmed={flatDifferentials.isConfirmed}
                    onSelect={handleFlatSelect}
                    onConfirm={handleFlatConfirm}
                    onChangeChoice={handleFlatChangeChoice}
                    conditions={AccordionData}
                    allowEmptyConfirm={true}
                    canEdit={canEdit}
                  />
                </div>
              )}

              {/* Formulation — shown for MDT review orgs (TOC) and CI Review
                  orgs (SLaM). SLaM uses the same amber "Emerging Formulation"
                  / "Not included in final report" treatment as SSI Review;
                  TOC keeps the grey Formulation Notes box for MDT discussion. */}
              {showNotesSections && (
                wf.hasCIReview ? (
                  <div className="bg-amber-50 rounded-2xl border border-amber-200 text-left px-6 pt-6 pb-2 shadow-md mt-6 flex flex-col">
                    <h3 className="font-bold text-lg mb-2">Emerging Formulation</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Your working hypothesis about what&apos;s going on for this
                      person and why. Capture the presenting picture, contributing
                      factors, functional impact and strengths. Finalised at
                      Diagnostic Decision.
                    </p>
                    <DictationTextArea
                      value={additionalNotes}
                      onChange={(val) => handleAdditionalNotesChange(val)}
                      onBlur={handleAdditionalNotesBlur}
                      canEdit={canEdit}
                      ariaLabel="Emerging formulation notes"
                    />
                    <p className="text-xs italic text-amber-900/80 text-right mt-1 mb-0">
                      Not included in final report
                    </p>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-2xl border border-gray-200 text-left p-6 shadow-md mt-6">
                    <h3 className="font-bold text-lg mb-2">Formulation Notes</h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Record your clinical reasoning and formulation thinking.
                      These notes will be visible during the MDT review as a
                      reference for discussion, but will not be included in the
                      report.
                    </p>
                    <DictationTextArea
                      value={additionalNotes}
                      onChange={(val) => handleAdditionalNotesChange(val)}
                      onBlur={handleAdditionalNotesBlur}
                      canEdit={canEdit}
                      ariaLabel="Formulation notes"
                    />
                  </div>
                )
              )}

              {/* Confidential notes — only for CI Review orgs (SLaM).
                  Private observations visible on the DD Review page but never
                  included in any patient-facing documentation. */}
              {wf.hasCIReview && (
                <div className="bg-amber-50 rounded-2xl border border-amber-200 text-left p-6 shadow-md mt-6">
                  <h3 className="font-bold text-lg mb-2">Confidential Notes</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Record any private clinical observations or concerns. These notes are <strong>strictly confidential</strong> — they will be visible to clinicians on the Review page during the Diagnostic Decision but will never appear in any patient-facing report.
                  </p>
                  <DictationTextArea
                    value={confidentialNotes}
                    onChange={(val) => handleConfidentialNotesChange(val)}
                    onBlur={handleConfidentialNotesBlur}
                    canEdit={canEdit}
                    ariaLabel="Confidential notes"
                  />
                </div>
              )}
            </>
          ) : (
            /* ─── Hierarchical DF: PPD / SCD / Differentials panels ─── */
            <div className="flex flex-col gap-6">
              <ConditionSelectionPanel
                title={config.panels.primaryDiagnosis.title}
                subtitle={config.panels.primaryDiagnosis.subtitle}
                selectedOptions={selections[PANEL_TYPES.PRIMARY].selected}
                isMultiSelect={false}
                isConfirmed={selections[PANEL_TYPES.PRIMARY].isConfirmed}
                onSelect={(value) => handleSelect(PANEL_TYPES.PRIMARY, value)}
                onConfirm={() => handleConfirm(PANEL_TYPES.PRIMARY)}
                onChangeChoice={() => handleChangeChoice(PANEL_TYPES.PRIMARY)}
                conditions={AccordionData}
                allowEmptyConfirm={false}
                canEdit={canEdit}
              />

              <div className={`space-y-6 transition-all duration-300 ease-in-out transform ${selections[PANEL_TYPES.PRIMARY].isConfirmed ?
                'opacity-100 translate-y-0' :
                'opacity-0 translate-y-4 h-0 overflow-hidden'}`}>

                <ConditionSelectionPanel
                  title={config.panels.comorbidDiagnoses.title}
                  subtitle={config.panels.comorbidDiagnoses.subtitle}
                  selectedOptions={selections[PANEL_TYPES.COMORBID].selected}
                  isMultiSelect={true}
                  isConfirmed={selections[PANEL_TYPES.COMORBID].isConfirmed}
                  onSelect={(value) => handleSelect(PANEL_TYPES.COMORBID, value)}
                  onConfirm={() => handleConfirm(PANEL_TYPES.COMORBID)}
                  onChangeChoice={() => handleChangeChoice(PANEL_TYPES.COMORBID)}
                  conditions={AccordionData}
                  isDisabled={(value) => (value === selections[PANEL_TYPES.PRIMARY].selected)}
                  allowEmptyConfirm={true}
                  canEdit={canEdit}
                />

                {config.panels.differentialDiagnoses.visible && (
                  <ConditionSelectionPanel
                    title={config.panels.differentialDiagnoses.title}
                    subtitle={config.panels.differentialDiagnoses.subtitle}
                    selectedOptions={selections[PANEL_TYPES.DIFFERENTIAL].selected}
                    isMultiSelect={true}
                    isConfirmed={selections[PANEL_TYPES.DIFFERENTIAL].isConfirmed}
                    onSelect={(value) => handleSelect(PANEL_TYPES.DIFFERENTIAL, value)}
                    onConfirm={() => handleConfirm(PANEL_TYPES.DIFFERENTIAL)}
                    onChangeChoice={() => handleChangeChoice(PANEL_TYPES.DIFFERENTIAL)}
                    conditions={AccordionData}
                    isDisabled={(value) => (value === selections[PANEL_TYPES.PRIMARY].selected)}
                    allowEmptyConfirm={true}
                    canEdit={canEdit}
                  />
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end items-center mt-4 pt-4 border-t border-gray-200">   
          {canEdit && (   
            <button
              onClick={handleNextSection}
              className={`flex items-center py-2 px-4 rounded-md shadow-md transition-all duration-300 ${isFormComplete && !isSaving
                ? 'hover:bg-violet-800 transition duration-100 bg-violet-900 text-white'
                : isSaving ? 'opacity-60 cursor-not-allowed bg-violet-900 text-white' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              disabled={!isFormComplete || isSaving}
            >
              <span className="font-bold">{isSaving ? "Saving Progress..." : `Finish Clinical Interview`}</span>
              {isSaving
                ? <ButtonSpinner className="w-5 h-5 ml-2" />
                : <CheckCircleIcon className="w-5 h-5 ml-2" aria-hidden="true" />}
              </button>
          )}
          </div>        
        </div>
      </div>

      {wf.hasCIReview ? (
        <CIFinishPathDialog
          open={isDialogOpen && !isGenerating}
          onOpenChange={setIsDialogOpen}
          differentials={withoutAdhd(flatDifferentials.selected)}
          formulation={additionalNotes}
          isSubmitting={isFinishPathSubmitting}
          onConfirmPadPath={handleConfirmPadPath}
          onGenerateReport={handleGenerateReportDeferPad}
        />
      ) : (
        <ModuleCompletionDialog
          open={isDialogOpen && !isGenerating}
          onOpenChange={setIsDialogOpen}
          title="Clinical Interview Complete"
          description="Review your diagnostic decision before proceeding to the next step."
          actions={
            <>
              <CancelButton onClick={() => setIsDialogOpen(false)} />
              {shouldShowGenerateReportButton() && (
                <GenerateReportButton onClick={handleGenerateReport} secondary />
              )}
              <PrimaryActionButton
                label={getNextButtonText()}
                onClick={() => handleNextPageButtonClick(`/patient/${patientId}${getNextRoute()}`)}
              />
            </>
          }
        >
          <SummarySection title={isFlatModel ? 'Conditions Under Consideration:' : 'Outcome:'}>
            {constructDiagnosisSummary()}
          </SummarySection>

          {isFlatModel && showNotesSections && additionalNotes.trim() && (
            <div className="mb-4">
              <h3 className="font-semibold mb-2">Formulation Notes:</h3>
              <div className="text-gray-700 text-sm whitespace-pre-wrap rounded-lg p-3 border bg-gray-50 border-gray-200">
                {additionalNotes}
              </div>
            </div>
          )}

          <DialogMessage>{getDialogMessage()}</DialogMessage>
        </ModuleCompletionDialog>
      )}

      <GenerateReportProgressDialog
        reportStage="Clinical Interview"
        patientId={patientId}
        clinicianId={clinicianId}
        handleNextPageButtonClick={() => handleNextPageButtonClick(`/patient/${patientId}/review-document-ci`)}
        isGenerating={isGenerating}
        onCancel={handleCancelGeneration}
      />

      {/* SSI Conflict Alert - 3 Borough / SLaM workflow only
          Shown when Lead clinician's provisional diagnosis conflicts with SSI recommendation */}
      {isSSIFirstOrg && (
        <SSIConflictAlert
          isOpen={isConflictAlertOpen}
          onClose={handleConflictAlertClose}
          conflictDetails={conflictDetails}
          onViewSSIReport={handleViewSSIReport}
          onResetSSI={handleResetSSI}
          isResetting={isResettingSSI}
        />
      )}

      {/* SSI Report Embed Dialog - shown when viewing SSI report from conflict alert */}
      <DocumentEmbedDialog
        isOpen={isSSIReportDialogOpen}
        onClose={() => setIsSSIReportDialogOpen(false)}
        reportStage="Semi-Structured Interview"
        clinicianId={auth.currentUser?.uid}
        patientId={patientId}
        lastGenerated={ssiLastGenerated}
      />

    </Layout>
  );
}

